"use client";

import { useState, type FormEvent } from "react";
import { PROJECT_TYPES, SITE } from "@/lib/site";

// The contact form posts to the public contact API (API Gateway → Lambda →
// DynamoDB + best-effort SMS). The URL is inlined at build time from
// NEXT_PUBLIC_CONTACT_API_URL (set by deploy.yml from a Terraform output).
const CONTACT_API = process.env.NEXT_PUBLIC_CONTACT_API_URL;

type Status = "idle" | "sending" | "ok" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // Honeypot — bots fill hidden fields; humans don't. Pretend success.
    if (data.get("website")) {
      setStatus("ok");
      form.reset();
      return;
    }

    const payload = {
      name: String(data.get("name") || "").trim(),
      phone: String(data.get("phone") || "").trim(),
      email: String(data.get("email") || "").trim(),
      projectType: String(data.get("projectType") || "").trim(),
      message: String(data.get("message") || "").trim(),
    };

    setStatus("sending");
    setError(null);
    try {
      if (!CONTACT_API) {
        throw new Error("The contact form isn't connected yet — please call us instead.");
      }
      const res = await fetch(`${CONTACT_API.replace(/\/$/, "")}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(
          body?.error ||
            "Sorry — something went wrong sending your message. Please try again or call us.",
        );
      }
      setStatus("ok");
      form.reset();
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Something went wrong.");
    }
  }

  if (status === "ok") {
    return (
      <div className="rounded-lg border border-cream-dark bg-cream p-8 text-center">
        <h2 className="font-display text-2xl font-bold text-navy">Message received!</h2>
        <p className="mt-3 text-ink/80">
          Thanks for reaching out — we&apos;ll be in touch shortly. For immediate help,
          call{" "}
          <a href={SITE.phoneHref} className="font-semibold text-gold-dark">
            {SITE.phone}
          </a>
          .
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className="form-label">
            Name <span className="text-gold-dark">*</span>
          </label>
          <input id="name" name="name" type="text" required className="form-input" />
        </div>
        <div>
          <label htmlFor="phone" className="form-label">
            Phone <span className="text-gold-dark">*</span>
          </label>
          <input id="phone" name="phone" type="tel" required className="form-input" />
        </div>
      </div>

      <div>
        <label htmlFor="email" className="form-label">
          Email <span className="text-gold-dark">*</span>
        </label>
        <input id="email" name="email" type="email" required className="form-input" />
      </div>

      <div>
        <label htmlFor="projectType" className="form-label">
          Project Type
        </label>
        <select id="projectType" name="projectType" className="form-input" defaultValue="">
          <option value="" disabled>
            Select an option
          </option>
          {PROJECT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="message" className="form-label">
          Message <span className="text-gold-dark">*</span>
        </label>
        <textarea id="message" name="message" rows={5} required className="form-input" />
      </div>

      {/* Honeypot — visually hidden, off the tab order. */}
      <div aria-hidden className="hidden">
        <label>
          Leave this field empty
          <input type="text" name="website" tabIndex={-1} autoComplete="off" />
        </label>
      </div>

      {status === "error" && error && (
        <p role="alert" className="text-sm font-medium text-red-700">
          {error}
        </p>
      )}

      <button type="submit" disabled={status === "sending"} className="btn-primary w-full text-sm uppercase sm:w-auto">
        {status === "sending" ? "Sending…" : "Send Message"}
      </button>
    </form>
  );
}
